import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  HardHat,
  CheckCircle,
  XCircle,
  ChevronRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useDeactivateHelmetType } from '@/features/reports/hooks';
import { CreateHelmetTypeModal } from './CreateHelmetTypeModal';
import { HelmetTypeDetailModal } from './HelmetTypeDetailModal';
import toast from 'react-hot-toast';
import { useModal } from '@/components/modal';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import type { HelmetTypeDefinition, HelmetTypeListResponse } from '@/features/reports/types';
import { PageHeader, StatCard, CardSkeleton, EmptyState, ErrorState, SearchInput, GlassSelect, CreateButton, BulkActionBar, StatusDot, InfiniteScrollSentinel } from '@/components/ui';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRTL } from '@/hooks/useRTL';
import { useSelectionSet } from '@/hooks/useSelectionSet';

const PAGE_SIZE = 20;

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════
export function HelmetsManagementPage() {
  const { t } = useTranslation();
  const { openConfirm } = useModal();
  const isRTL = useRTL();

  const { hasPermission } = usePermissions();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<HelmetTypeDefinition | null>(null);

  const queryClient = useQueryClient();

  const deleteMutation = useDeactivateHelmetType();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['helmetTypes', 'infinite', search, statusFilter],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, unknown> = {
        page: pageParam,
        limit: PAGE_SIZE,
      };
      if (search) params.search = search;
      if (statusFilter) params.isActive = statusFilter;

      const { data } = await http.get<HelmetTypeListResponse>('/helmet-types', { params });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.helmetTypes.length, 0);
      return loaded < lastPage.pagination.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage });

  const allTypes = useMemo(
    () => data?.pages.flatMap((page) => page.helmetTypes) || [],
    [data]
  );

  const { selectedIds, isSelected, toggle, selectAll, clear, count, allSelected } = useSelectionSet(allTypes);

  const totalCount = data?.pages[0]?.pagination.total || 0;
  const activeCount = useMemo(
    () => allTypes.filter((ht) => ht.isActive).length,
    [allTypes]
  );
  const inactiveCount = useMemo(
    () => allTypes.filter((ht) => !ht.isActive).length,
    [allTypes]
  );
  const totalReports = useMemo(
    () => allTypes.reduce((sum, ht) => sum + (ht.reportCount ?? 0), 0),
    [allTypes]
  );

  const handleBulkDelete = () => {
    triggerHapticFeedback();
    openConfirm({
      title: t('helmetsAdmin.bulkDeleteTitle'),
      message: t('helmetsAdmin.bulkDeleteMessage', { count }),
      variant: 'danger',
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        await Promise.all(Array.from(selectedIds).map(id => deleteMutation.mutateAsync(id)));
        toast.success(t('helmetsAdmin.bulkDeletedSuccess'));
        clear();
      },
    });
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4 md:space-y-6 pb-24"
    >
      {/* ─── Header ──────────────────────────────────────────── */}
      <motion.div variants={staggerItem}>
        <PageHeader
          icon={<HardHat className="w-7 h-7" />}
          title={t('helmetsAdmin.management')}
          action={
            hasPermission('action:manage_helmets') ? (
              <CreateButton
                label={t('helmetsAdmin.addType')}
                onClick={() => {
                  triggerHapticFeedback();
                  setCreateModalOpen(true);
                }}
              />
            ) : undefined
          }
        />
      </motion.div>

      {/* ─── Stats Cards ─────────────────────────────────────── */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<HardHat className="w-7 h-7 md:w-8 md:h-8" />} label={t('helmetsAdmin.totalTypes')} value={totalCount} />
        <StatCard icon={<CheckCircle className="w-7 h-7 md:w-8 md:h-8" />} label={t('helmetsAdmin.active')} value={activeCount} />
        <StatCard icon={<XCircle className="w-7 h-7 md:w-8 md:h-8" />} label={t('helmetsAdmin.inactive')} value={inactiveCount} />
        <StatCard icon={<HardHat className="w-7 h-7 md:w-8 md:h-8" />} label={t('helmetsAdmin.totalReports')} value={totalReports} />
      </motion.div>

      {/* ─── Search & Filters ────────────────────────────────── */}
      <motion.div
        variants={staggerItem}
        className="rounded-3xl overflow-hidden glass-surface"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div
          className="px-6 py-2.5"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            borderBottom: `1px solid ${ADMIN_COLORS.border}`,
          }}
        >
          <h2 className="font-semibold text-xs sm:text-sm">
            {t('common.filters')}
          </h2>
        </div>
        <div className="px-6 py-4 flex flex-col md:flex-row gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder={t('helmetsAdmin.searchPlaceholder')} />

          <button onClick={selectAll} className="flex items-center gap-2 px-3 py-2 border border-white/30 rounded-lg bg-white/20 cursor-pointer hover:bg-white/20 transition-colors">
            <input type="checkbox" checked={allSelected} onChange={selectAll} className="w-5 h-5 pointer-events-none" readOnly />
            <span className="font-bold text-sm">{t('helmetsAdmin.selectAll')}</span>
          </button>

          <GlassSelect
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              triggerHapticFeedback();
            }}
          >
            <option value="">{t('helmetsAdmin.allStatuses')}</option>
            <option value="true">{t('helmetsAdmin.active')}</option>
            <option value="false">{t('helmetsAdmin.inactive')}</option>
          </GlassSelect>
        </div>
      </motion.div>

      {/* ─── Helmet Type List ──────────────────────────────── */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </motion.div>
        ) : isError ? (
          <ErrorState message={t('helmetsAdmin.loadError')} />
        ) : allTypes.length === 0 ? (
          <EmptyState icon={<HardHat className="w-14 h-14 md:w-16 md:h-16" />} message={t('helmetsAdmin.noTypes')} />
        ) : (
          <motion.div
            key="content"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {allTypes.map((ht) => {
              const selected = isSelected(ht.id);

              return (
                <motion.div
                  key={ht.id}
                  role="button"
                  tabIndex={0}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    triggerHapticFeedback();
                    setSelectedType(ht);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      triggerHapticFeedback();
                      setSelectedType(ht);
                    }
                  }}
                  className="flex items-center justify-between w-full py-1.5 ps-6 pe-4 md:py-2 md:ps-6 md:pe-4 rounded-xl text-start cursor-pointer border border-white/30 glass-surface"
                  style={{
                    boxShadow: selected
                      ? ADMIN_COLORS.shadowMd
                      : undefined,
                  }}
                >
                  {/* Left section */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Checkbox — large tap target */}
                    <div
                      className="flex-shrink-0 flex items-center justify-center cursor-pointer"
                      style={{ minWidth: '44px', minHeight: '44px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(ht.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggle(ht.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-5 h-5 pointer-events-none"
                      />
                    </div>

                    {/* Icon circle — always neoLightBlue (no color field) */}
                    <div
                      className="w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{
                        backgroundColor: ADMIN_COLORS.surface,
                        border: `1px solid ${ADMIN_COLORS.border}`,
                      }}
                    >
                      <HardHat className="w-5 h-5 md:w-6 md:h-6" style={{ color: '#64748b' }} />
                    </div>

                    {/* Text content */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-xs md:text-sm text-neutral-800 truncate max-w-[160px] md:max-w-none">
                        {ht.name}
                      </p>
                    </div>
                  </div>

                  {/* Right section */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Report count (desktop only) */}
                    <div className="hidden md:flex items-center gap-1 text-sm font-medium text-slate-800/70">
                      <HardHat className="w-4 h-4" />
                      {ht.reportCount ?? 0}
                    </div>

                    {/* Status dot */}
                    <StatusDot
                      active={ht.isActive}
                      title={ht.isActive ? t('helmetsAdmin.active') : t('helmetsAdmin.inactive')}
                    />

                    {/* Chevron */}
                    <ChevronRight
                      className="w-5 h-5 text-slate-500"
                      style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Infinite scroll sentinel ─────────────────────────── */}
      <InfiniteScrollSentinel hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} sentinelRef={sentinelRef} />

      {/* ─── Modals ───────────────────────────────────────────── */}
      <CreateHelmetTypeModal
        isOpen={createModalOpen}
        onClose={() => { queryClient.refetchQueries({ queryKey: ['helmetTypes'] }); setCreateModalOpen(false); }}
        onSuccess={() => {}}
      />

      {selectedType && (
        <HelmetTypeDetailModal
          helmetType={selectedType}
          onClose={() => { queryClient.refetchQueries({ queryKey: ['helmetTypes'] }); setSelectedType(null); }}
        />
      )}

      {/* ─── Bulk Action Bar ───────────────── */}
      <BulkActionBar count={count} onDelete={handleBulkDelete} onClear={clear} isDeleting={deleteMutation.isPending} />
    </motion.div>
  );
}
